import { useState, useEffect } from "react";
import { ShieldCheck, ShieldOff, ShieldX, Loader2, Info } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useToast } from "./Toast";
import { getAccessBadge } from "../lib/accessStatus";
import ConfirmDeleteDrawer from "./ConfirmDeleteDrawer";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Tarjeta de control de acceso del alumno (Fase 5).
// Deriva el estado de las columnas ya cargadas y escribe vía la edge
// set-student-access. No enciende ni lee lógica del gate más allá del aviso.
export default function StudentAccessCard({ student, onUpdated }) {
  const { showSuccess, showError } = useToast();
  const [busy, setBusy] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [gateEnabled, setGateEnabled] = useState(null);

  const access = getAccessBadge(student);
  // "Quitar acceso" solo si aún tiene acceso (activo).
  // "Rehabilitar" si ya no lo tiene (caducado o retirado). Nunca ambos.
  const canRevoke = access.status === "activo";
  const canRestore = access.status === "caducado" || access.status === "retirado";

  useEffect(() => {
    let active = true;
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "access_gate_enabled")
      .maybeSingle()
      .then(({ data }) => {
        if (active) setGateEnabled(data?.value === true);
      });
    return () => {
      active = false;
    };
  }, []);

  const callEdge = async (action) => {
    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/set-student-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ student_id: student.id, action }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.ok || data.error)
        throw new Error(data.error || "Error al actualizar el acceso");

      onUpdated({ ...student, ...data.student });
      showSuccess(
        action === "revoke" ? "Acceso retirado" : "Acceso rehabilitado"
      );
    } catch (err) {
      showError(err.message || "Error al actualizar el acceso");
    } finally {
      setBusy(false);
      setShowRevokeConfirm(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Acceso</h3>
          <span
            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${access.color}`}
          >
            {access.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canRevoke && (
            <button
              onClick={() => setShowRevokeConfirm(true)}
              disabled={busy}
              className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ShieldOff size={16} />
              )}
              Quitar acceso
            </button>
          )}
          {canRestore && (
            <button
              onClick={() => callEdge("restore")}
              disabled={busy}
              className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ShieldCheck size={16} />
              )}
              Rehabilitar acceso
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Caduca</span>
          <p className="font-medium text-gray-700">
            {formatDate(student.access_expires_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {student.access_revoked_at ? (
            <>
              <ShieldX size={16} className="text-red-500" />
              <span className="text-gray-500">
                Retirado el {formatDate(student.access_revoked_at)}
              </span>
            </>
          ) : (
            <>
              <ShieldCheck size={16} className="text-emerald-500" />
              <span className="text-gray-500">Sin retirar</span>
            </>
          )}
        </div>
      </div>

      {gateEnabled === false && (
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          <Info size={14} className="shrink-0 mt-0.5 text-gray-400" />
          <span>
            El control de acceso aún no está activo; los estados son
            informativos.
          </span>
        </div>
      )}

      {showRevokeConfirm && (
        <ConfirmDeleteDrawer
          title="Quitar acceso"
          itemName={student.name}
          message="El alumno pasará a estado Retirado. Podrás rehabilitar su acceso más adelante."
          warningText="El acceso real solo se bloquea cuando el control de acceso esté activo."
          confirmLabel="Quitar acceso"
          onConfirm={() => callEdge("revoke")}
          onCancel={() => setShowRevokeConfirm(false)}
        />
      )}
    </div>
  );
}
